import * as React from 'react';
import { deepEquals } from 'rcd-cpp-workspace/utils/deepEquals';
import { cn } from '../../utils';
import { Dropbox, DropboxProps } from '../Dropbox/dropbox';
import './dropdown.scss';
import { ThreeDots } from '../Icon/icon';
import { ButtonProps, Button } from 'rcd-cpp-ui/ui/button/Button';

type Props<T = any> = Pick<
  React.HTMLAttributes<HTMLDivElement>,
  'className' | 'children'
> &
  Pick<DropboxProps, 'position' | 'forceShow' | 'onStateChanged'> & {
    options: T[];
    renderOption?: (t: T) => any;
    onSelect: (t: T) => void;
    selected?: T;
    position?: 'right';
    disabled?: boolean | ((t: T) => boolean);
    renderIcon?: (t: T) => any;
  };

const defaultRenderOption = (o: any) => {
  if (!o) return o;
  if (typeof o == 'string') return o;
  return o.label || o.name || o.value;
};

export const ActionDropdown = <T,>(p: Omit<Props<T>, 'children'>) => (
  <Dropdown {...p} className="action-dropdown">
    <div>
      <ThreeDots />
    </div>
  </Dropdown>
);

const doNothing = () => null;

export const ButtonDropdown = <T,>({
  label,
  buttonType,
  buttonSize,
  ...p
}: Omit<Props<T>, 'children'> &
  Pick<ButtonProps, 'label' | 'buttonType' | 'buttonSize'>) => (
  <Dropdown {...p} className="button-dropdown">
    <Button
      label={label}
      className={p.className}
      buttonType={buttonType}
      buttonSize={buttonSize}
      onClick={doNothing}
    />
  </Dropdown>
);

export const Dropdown = <T,>({
  className,
  children,
  options,
  selected,
  position,
  renderOption = defaultRenderOption,
  renderIcon,
  onSelect,
  disabled: _disabled,
  ...p
}: Props<T>) => {
  const isDisabled = React.useCallback(
    (t: T) => {
      if (typeof _disabled == 'function') {
        return _disabled(t);
      }
      return _disabled;
    },
    [_disabled]
  );
  if (options == null) return null;
  return (
    <Dropbox
      {...p}
      disabled={typeof _disabled == 'boolean' ? _disabled : undefined}
      position={position}
      className={cn({ [className]: !!className }, 'dd-dropdown')}
      renderBox={setExpanded => (
        <ul className="dd-list">
          {options?.map((o, idx) => {
            const opt = renderOption(o);
            const ddText = typeof opt === 'string';
            const optDisabled = isDisabled(o);
            return (
              <li
                key={idx}
                className={cn({
                  'dd-disabled': optDisabled,
                  'dd-selected': deepEquals(o, selected),
                  'dd-text': ddText && !renderIcon
                })}
                onClick={() => {
                  if (optDisabled) return;
                  if (onSelect) onSelect(o);
                  setExpanded(false);
                }}
                data-txt={ddText ? `-${opt}-` : undefined}
              >
                {!!renderIcon ? (
                  <div className="dd-opt">
                    <span className="dd-icon">{renderIcon(o)}</span>

                    <span
                      className={cn({ 'dd-text': ddText })}
                      data-txt={ddText ? `-${opt}-` : undefined}
                    >
                      {opt}
                    </span>
                  </div>
                ) : (
                  opt
                )}
              </li>
            );
          })}
        </ul>
      )}
    >
      {children}
    </Dropbox>
  );
};
